import { Alert, AlertColor, Snackbar } from '@mui/material';
import React, { createContext, useContext, useState } from 'react';

interface NotificationState {
    open: boolean;
    message: string;
    severity: AlertColor;
}

interface NotificationContextType {
    showNotification: (message: string, severity?: AlertColor) => void;
    hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notification, setNotification] = useState<NotificationState>({
        open: false,
        message: '',
        severity: 'info',
    });

    const showNotification = (message: string, severity: AlertColor = 'info') => {
        setNotification({
            open: true,
            message,
            severity,
        });
    };

    const hideNotification = () => {
        setNotification((prev) => ({
            ...prev,
            open: false,
        }));
    };

    return (
        <NotificationContext.Provider value={{ showNotification, hideNotification }}>
            {children}
            <Snackbar
                open={notification.open}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={6000}
                onClose={hideNotification}
            >
                <Alert 
                    onClose={hideNotification} 
                    severity={notification.severity} 
                    sx={{
                        width: '100%',
                        boxShadow: 3,
                        borderRadius: 1
                    }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    );
};
